import React, { useEffect, useRef, useState } from 'react';

// "Todo lo que incluye H.E.R.": carrusel de cards que se mueve solo (y se puede arrastrar).
// Se usa en el hero "story", justo debajo del primer botón. Con "reducir movimiento" no se mueve solo:
// se muestra como una tira que se desliza con el dedo. Las cards no abren previews.

const CDN = 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/';
const R2 = 'https://archivos.simpleymejor.com/';
const DULCE_COVER = R2 + 'httpsarchivos.simpleymejor.comhercoversher-dulce-cover.png.png';
const RECETARIO_COVER = R2 + 'hercoversher-recetario-cover.png.png';

// PORTADA DE LA GUÍA: el archivo se subió como "Guía H·E·R· en cobre y negro.png" y todavía no se sabe
// con certeza cómo quedó guardada la clave en R2 (tilde, puntos medios). Se prueban en orden estas
// variantes y se usa la primera que carga. Cuando se sepa cuál funciona, dejar solo esa (o renombrar el
// archivo a her-guia-cover.png y usar solo la primera).
const GUIA_NAME = 'Guía H·E·R· en cobre y negro.png';
const GUIA_COVERS = [R2 + 'her-guia-cover.png', R2 + encodeURIComponent(GUIA_NAME), R2 + encodeURIComponent(GUIA_NAME.normalize('NFD')), R2 + 'her-ebooks/' + encodeURIComponent(GUIA_NAME), R2 + encodeURIComponent('Guía H.E.R. en cobre y negro.png')];
type CardItem = {
  module: string;
  title: string;
  benefit: string;
  imgs: string[];
  pos: string;
};
const cards: CardItem[] = [{
  module: 'guia',
  title: 'Guía H.E.R.',
  benefit: '✓ 9 módulos para entender esta etapa',
  imgs: GUIA_COVERS,
  pos: 'object-[center_30%]'
}, {
  module: 'recetario',
  title: 'Recetario H.E.R.',
  benefit: '✓ Comidas simples para 21 días',
  imgs: [RECETARIO_COVER],
  pos: 'object-[center_30%]'
}, {
  module: 'movement',
  title: 'H.E.R. Movement',
  benefit: '✓ Movimiento suave, a tu ritmo',
  imgs: [CDN + 'S8SNVUZHdzf8t092yIlAK_430x.jpg?v=1780543245'],
  pos: 'object-[center_34%]'
}, {
  module: 'noche',
  title: 'Noche en Calma',
  benefit: '✓ 7 noches para preparar tu descanso',
  imgs: [CDN + 'qushb3WzLl3BYpA9n1eL_430x.jpg?v=1780543244'],
  pos: 'object-[center_42%]'
}, {
  module: 'tracker',
  title: 'Tracker Premium',
  benefit: '✓ Registrá cómo te sentís',
  imgs: [CDN + 'v5gVLGm07qY3WP7hCr5Mp_430x.jpg?v=1780543245'],
  pos: 'object-[center_18%]'
}, {
  module: 'dulce',
  title: 'H.E.R. Dulce',
  benefit: '✓ 36 postres para disfrutar lo dulce',
  imgs: [DULCE_COVER],
  pos: 'object-[center_30%]'
}];

// Imagen con respaldo: prueba cada dirección en orden; si ninguna carga, muestra el título sobre fondo oscuro.
function CardImage({
  srcs,
  alt,
  className,
  label
}: {
  srcs: string[];
  alt: string;
  className: string;
  label: string;
}) {
  const [i, setI] = useState(0);
  if (i >= srcs.length) {
    return <div aria-hidden={!alt} className="flex h-full w-full items-center justify-center bg-[#090807] px-6 text-center font-display text-[24px] text-[#C8A870]" data-id="element-357" data-mp="96pr24wjiruz">
        {label}
      </div>;
  }
  return <img src={srcs[i]} alt={alt} loading="lazy" decoding="async" draggable={false} onError={() => setI(n => n + 1)} className={className} data-id="element-358" data-mp="3crwso6lkzsn7" />;
}
const SECONDS_PER_CARD = 10;
const RESUME_DELAY_MS = 1800;
export function IncludesCarousel() {
  // Con "reducir movimiento": sin movimiento automático; se desliza con el dedo como una tira normal.
  const [reduce] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const halfWidthRef = useRef(0);
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragStartOffsetRef = useRef(0);
  const resumeTimeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  useEffect(() => {
    const track = trackRef.current;
    const container = containerRef.current;
    if (!track || !container) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const clearResumeTimer = () => {
      if (resumeTimeoutRef.current !== null) {
        window.clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
    const scheduleResume = () => {
      clearResumeTimer();
      resumeTimeoutRef.current = window.setTimeout(() => {
        isPausedRef.current = false;
        resumeTimeoutRef.current = null;
      }, RESUME_DELAY_MS);
    };
    const measure = () => {
      halfWidthRef.current = track.scrollWidth / 2;
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(track);
    const applyTransform = () => {
      track.style.transform = `translate3d(${-offsetRef.current}px,0,0)`;
    };
    const step = (ts: number) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;
      const halfWidth = halfWidthRef.current;
      if (halfWidth > 0) {
        if (!reduceMotion && !isPausedRef.current && !isDraggingRef.current) {
          const pxPerMs = halfWidth / (cards.length * SECONDS_PER_CARD * 1000);
          offsetRef.current += pxPerMs * delta;
        }
        if (offsetRef.current >= halfWidth) offsetRef.current -= halfWidth;
        if (offsetRef.current < 0) offsetRef.current += halfWidth;
        applyTransform();
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    const handlePointerDown = (e: PointerEvent) => {
      isDraggingRef.current = true;
      isPausedRef.current = true;
      clearResumeTimer();
      dragStartXRef.current = e.clientX;
      dragStartOffsetRef.current = offsetRef.current;
      try {
        container.setPointerCapture(e.pointerId);
      } catch {}
    };
    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current || dragStartXRef.current === null) return;
      offsetRef.current = dragStartOffsetRef.current - (e.clientX - dragStartXRef.current);
    };
    const endDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      dragStartXRef.current = null;
      scheduleResume();
    };
    container.addEventListener('pointerdown', handlePointerDown, {
      passive: true
    });
    container.addEventListener('pointermove', handlePointerMove, {
      passive: true
    });
    container.addEventListener('pointerup', endDrag, {
      passive: true
    });
    container.addEventListener('pointercancel', endDrag, {
      passive: true
    });
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      clearResumeTimer();
      resizeObserver.disconnect();
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', endDrag);
      container.removeEventListener('pointercancel', endDrag);
    };
  }, []);
  if (reduce) {
    return <section className="bg-gradient-hero px-5 pb-12 pt-4" data-id="element-359" data-mp="12t6gwx36ngr3">
        <div className="mx-auto w-full max-w-[480px] text-center" data-id="element-360" data-mp="xs930wp7h8ve">
          <p className="mb-3 text-center uppercase tracking-[0.22em] text-[#c9a96e]" style={{
          fontSize: '15px'
        }} data-id="element-361" data-mp="stfod0y6nbtn">
            Biblioteca Digital H.E.R.
          </p>
          <h2 className="m-0 text-center font-serif text-[32px] leading-[1.08]" data-id="element-362" data-mp="1lhjybynseb25">
            <em className="italic text-[#c9a96e]" data-id="element-363" data-mp="1ljo0ab24kzzs">Todo lo que incluye H.E.R.</em>
          </h2>
          <div className="scrollbar-hide -mx-5 mt-6 snap-x snap-mandatory overflow-x-auto px-5 pb-2" data-id="element-364" data-mp="1go8nznjrnc5h">
            <div className="flex w-max gap-4" data-id="element-365" data-mp="1bpntm4r73moy">
              {cards.map(item => <article key={item.module} className="w-[72vw] max-w-[360px] shrink-0 snap-center overflow-hidden rounded-[18px] border border-[#c9a96e]/20 bg-[#141210]" data-id="element-366" data-mp="298gw87iqz7ue">
                  <div className="h-[220px] overflow-hidden bg-[#090807]" data-id="element-367" data-mp="1crjprrff48u2">
                    <CardImage srcs={item.imgs} alt={item.title} label={item.title} className={`h-full w-full object-cover ${item.pos}`} data-id="element-368" data-mp="1hj1hdovjwx2d" />
                  </div>
                  <div className="px-[18px] py-4" style={{
                minHeight: '68px'
              }} data-id="element-369" data-mp="13ptmnoc6blgg">
                    <h3 className="sr-only" data-id="element-370" data-mp="17r5fe2qcgmsv">{item.title}</h3>
                    <p className="mt-1 text-center font-medium leading-snug text-[#c9a96e]" style={{
                  fontSize: '17px'
                }} data-id="element-371" data-mp="1vkdt1o3oicig">
                      {item.benefit}
                    </p>
                  </div>
                </article>)}
            </div>
          </div>
        </div>
      </section>;
  }
  return <section className="bg-gradient-hero px-5 pb-12 pt-4" data-id="element-372" data-mp="1rjlhgo7c3vj0">
      <div className="mx-auto w-full max-w-[480px] text-center" data-id="element-373" data-mp="b2fxwdimpw7f">
        <div className="mx-auto w-full max-w-[500px] overflow-hidden" aria-label="Biblioteca H.E.R." data-id="element-374" data-mp="g22duupudqjx">
          <p className="mb-3 text-center uppercase tracking-[0.22em] text-[#c9a96e]" style={{
          fontSize: '15px'
        }} data-id="element-375" data-mp="7ks8epl2rc22">
            Biblioteca Digital H.E.R.
          </p>

          <h2 className="m-0 text-center font-serif text-[32px] leading-[1.08]" data-id="element-376" data-mp="hjhj9yejzw8t">
            <em className="italic text-[#c9a96e]" data-id="element-377" data-mp="hh51bl8splvr">El sistema completo</em>
          </h2>

          <div ref={containerRef} className="relative -mx-5 mt-6 overflow-hidden px-5" style={{
          touchAction: 'pan-y',
          cursor: 'grab'
        }} data-id="element-378" data-mp="cibm9odv1mth">
            <div ref={trackRef} className="flex w-max gap-4 will-change-transform" aria-label="Recursos incluidos en la Biblioteca Digital H.E.R." data-id="element-379" data-mp="3g26ba6rkh05w">
              {[...cards, ...cards].map((item, index) => <article key={`${item.module}-${index}`} aria-hidden={index >= cards.length} className="w-[72vw] max-w-[360px] shrink-0 overflow-hidden rounded-[18px] border border-[#c9a96e]/20 bg-[#141210]" data-id="element-380" data-mp="3py9llzwko53x" data-mpi={index}>
                  <div className="h-[220px] overflow-hidden bg-[#090807]" data-id="element-381" data-mp="2x9u7dfl9lqtn">
                    <CardImage srcs={item.imgs} alt={index < cards.length ? item.title : ''} label={item.title} className={`pointer-events-none h-full w-full object-cover ${item.pos}`} data-id="element-382" data-mp="ps3k7vupqf30" />
                  </div>

                  <div className="px-[18px] py-4" style={{
                minHeight: '68px'
              }} data-id="element-383" data-mp="fscrnzppqa5k">
                    <h3 className="sr-only" data-id="element-384" data-mp="ati0oahldfad">{item.title}</h3>
                    <p className="mt-1 text-center font-medium leading-snug text-[#c9a96e]" style={{
                  fontSize: '17px'
                }} data-id="element-385" data-mp="6sfzvp810v6a">
                      {item.benefit}
                    </p>
                  </div>
                </article>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}